import type { CreditState, Shipment, Subscription } from "api";
import cn from "classnames";
import { creditAttention } from "components/Billing/credits.model";
import type { LoadStatus } from "hooks/useProfile";
import type { AttentionItem, GotoTarget } from "./dashboard.model";
import { attentionItems } from "./dashboard.model";
import { DashboardCard } from "./DashboardCard";

type AttentionCardProps = {
  shipments: Shipment[];
  shipmentsStatus: LoadStatus;
  subscriptions: Subscription[];
  subscriptionsStatus: LoadStatus;
  /** Billing lines join in once the balance is known. */
  credits: CreditState | null;
  onGoto: (target: GotoTarget) => void;
};

/** The worse of the two loads decides what the card can honestly show. */
const combine = (a: LoadStatus, b: LoadStatus): LoadStatus => {
  const order: LoadStatus[] = ["unauthorized", "failed", "unverified", "loading", "ready"];

  return order.find((status) => a === status || b === status) ?? "ready";
};

/**
 * Delays, subscriptions running out, and cards the carrier has not answered on.
 * An account with none of those gets told so — an empty list here is good news,
 * not a missing card.
 */
export const AttentionCard = ({
  shipments,
  shipmentsStatus,
  subscriptions,
  subscriptionsStatus,
  credits,
  onGoto,
}: AttentionCardProps) => {
  const status = combine(shipmentsStatus, subscriptionsStatus);
  const billing = credits === null ? { first: [], last: [] } : creditAttention(credits);
  const toItem = (item: (typeof billing.first)[number]): AttentionItem => ({
    ...item,
    action: { label: item.action, goto: { pane: "billing" } },
  });
  // A failing payment outranks everything; an upcoming charge is only news.
  const items = [
    ...billing.first.map(toItem),
    ...attentionItems(shipments, subscriptions),
    ...billing.last.map(toItem),
  ];

  return (
    <DashboardCard
      wide
      title="Needs attention"
      sub={
        items.length === 0
          ? "Nothing is waiting on you"
          : `${items.length} ${items.length === 1 ? "item" : "items"} across shipments, subscriptions and billing`
      }
      status={status}
      isEmpty={items.length === 0}
      empty="Nothing needs your attention right now."
      failure="Could not load what needs attention. Please refresh the page."
    >
      <ul className="dsh__att">
        {items.map((item) => (
          <li key={item.id}>
            <span
              className={cn("dsh__dot", {
                "dsh__dot--bad": item.tone === "bad",
                "dsh__dot--warn": item.tone === "warn",
              })}
            />
            <div>
              <b>{item.title}</b>
              <em>{item.detail}</em>
            </div>
            <button type="button" className="pcsh__lnk" onClick={() => onGoto(item.action.goto)}>
              {item.action.label}
            </button>
          </li>
        ))}
      </ul>
    </DashboardCard>
  );
};
